/*
copied here because it seems abandoned and was depending on an old version of quill
which in turn was introducing vulnerabilities in the dependency tree
React-Quill
https://github.com/zenoamaro/react-quill
*/

import Quill from 'quill';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Deep equality comparison helper
function isEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
}

// Small helper to execute a function in the next micro-tick
function postpone(fn) {
  Promise.resolve().then(fn);
}

const ReactQuill = React.forwardRef((props, ref) => {
  const {
    bounds,
    children,
    className = '',
    defaultValue,
    formats,
    id,
    modules = {},
    onChange,
    onChangeSelection,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyPress,
    onKeyUp,
    placeholder,
    preserveWhitespace,
    readOnly = false,
    scrollingContainer,
    style,
    tabIndex,
    theme = 'snow',
    value,
  } = props;

  const [generation, setGeneration] = useState(0);

  const editorRef = useRef(null);
  const editingAreaRef = useRef(null);
  const valueRef = useRef(value !== undefined ? value : defaultValue ?? '');
  const selectionRef = useRef(null);
  const lastDeltaChangeSetRef = useRef(null);
  const unprivilegedEditorRef = useRef(null);
  const prevPropsRef = useRef({});

  const dirtyProps = ['modules', 'formats', 'bounds', 'theme', 'children'];

  // Check if component is controlled
  const isControlled = useCallback(() => {
    return value !== undefined;
  }, [value]);

  // Validate props
  const validateProps = useCallback(() => {
    if (React.Children.count(children) > 1) {
      throw new Error('The Quill editing area can only be composed of a single React element.');
    }

    if (React.Children.count(children)) {
      const child = React.Children.only(children);
      if (child?.type === 'textarea') {
        throw new Error('Quill does not support editing on a <textarea>. Use a <div> instead.');
      }
    }

    if (lastDeltaChangeSetRef.current && value === lastDeltaChangeSetRef.current) {
      throw new Error(
        'You are passing the `delta` object from the `onChange` event back ' +
          'as `value`. You most probably want `editor.getContents()` instead. ' +
          'See: https://github.com/zenoamaro/react-quill#using-deltas'
      );
    }
  }, [children, value]);

  // Check if value is a Delta
  const isDelta = useCallback((val) => {
    return val && val.ops;
  }, []);

  // Special comparison function that knows how to compare Deltas
  const isEqualValue = useCallback(
    (val1, val2) => {
      if (isDelta(val1) && isDelta(val2)) {
        return isEqual(val1.ops, val2.ops);
      }
      return isEqual(val1, val2);
    },
    [isDelta]
  );

  // Create unprivileged editor proxy
  const makeUnprivilegedEditor = useCallback((editor) => {
    return {
      getHTML: () => editor.root.innerHTML,
      getLength: editor.getLength.bind(editor),
      getText: editor.getText.bind(editor),
      getContents: editor.getContents.bind(editor),
      getSelection: editor.getSelection.bind(editor),
      getBounds: editor.getBounds.bind(editor),
    };
  }, []);

  // Set editor contents
  const setEditorContents = useCallback((editor, val) => {
    if (!editor) return;
    valueRef.current = val;
    const sel = selectionRef.current;

    if (typeof val === 'string') {
      editor.setContents(editor.clipboard.convert(val));
    } else {
      editor.setContents(val);
    }

    postpone(() => {
      if (sel && editor) {
        const length = editor.getLength();
        sel.index = Math.max(0, Math.min(sel.index, length - 1));
        sel.length = Math.max(0, Math.min(sel.length, length - 1 - sel.index));
        editor.setSelection(sel);
        selectionRef.current = sel;
      }
    });
  }, []);

  // Set editor read-only state
  const setEditorReadOnly = useCallback((editor, isReadOnly) => {
    if (!editor) return;
    if (isReadOnly) {
      editor.disable();
    } else {
      editor.enable();
    }
  }, []);

  // Handle text change
  const onEditorChangeText = useCallback(
    (htmlValue, delta, source, editor) => {
      if (!editorRef.current) return;

      const nextContents = isDelta(valueRef.current) ? editor.getContents() : editor.getHTML();

      if (nextContents !== valueRef.current) {
        lastDeltaChangeSetRef.current = delta;
        valueRef.current = nextContents;
        onChange?.(htmlValue, delta, source, editor);
      }
    },
    [isDelta, onChange]
  );

  // Handle selection change
  const onEditorChangeSelection = useCallback(
    (nextSelection, source, editor) => {
      if (!editorRef.current) return;

      const currentSelection = selectionRef.current;
      const hasGainedFocus = !currentSelection && nextSelection;
      const hasLostFocus = currentSelection && !nextSelection;

      if (isEqual(nextSelection, currentSelection)) return;

      selectionRef.current = nextSelection;
      onChangeSelection?.(nextSelection, source, editor);

      if (hasGainedFocus) {
        onFocus?.(nextSelection, source, editor);
      } else if (hasLostFocus) {
        onBlur?.(currentSelection, source, editor);
      }
    },
    [onChangeSelection, onFocus, onBlur]
  );

  // Main editor change handler
  const onEditorChange = useCallback(
    (eventName, rangeOrDelta, oldRangeOrDelta, source) => {
      if (eventName === 'text-change') {
        onEditorChangeText(editorRef.current.root.innerHTML, rangeOrDelta, source, unprivilegedEditorRef.current);
      } else if (eventName === 'selection-change') {
        onEditorChangeSelection(rangeOrDelta, source, unprivilegedEditorRef.current);
      }
    },
    [onEditorChangeText, onEditorChangeSelection]
  );

  // Create and initialize editor
  useEffect(() => {
    validateProps();

    if (!editingAreaRef.current) return;

    const element = editingAreaRef.current;
    if (!element) {
      throw new Error('Cannot find element for editing area');
    }
    if (element.nodeType === 3) {
      throw new Error('Editing area cannot be a text node');
    }

    const config = {
      bounds,
      formats,
      modules,
      placeholder,
      readOnly,
      scrollingContainer,
      tabIndex,
      theme,
    };

    const editor = new Quill(element, config);

    if (config.tabIndex != null && editor?.scroll?.domNode) {
      editor.scroll.domNode.tabIndex = config.tabIndex;
    }

    editorRef.current = editor;
    unprivilegedEditorRef.current = makeUnprivilegedEditor(editor);

    editor.on('editor-change', onEditorChange);

    setEditorContents(editor, valueRef.current);

    return () => {
      if (editor) {
        editor.off('editor-change', onEditorChange);
      }
    };
  }, [generation]); // Re-run when generation changes (dirty props change)

  // Handle value changes
  useEffect(() => {
    if (!editorRef.current) return;
    if (value === undefined) return;

    const prevContents = valueRef.current;
    const nextContents = value ?? '';

    if (!isEqualValue(nextContents, prevContents)) {
      setEditorContents(editorRef.current, nextContents);
    }
  }, [value, isEqualValue, setEditorContents]);

  // Handle readOnly changes
  useEffect(() => {
    if (editorRef.current) {
      setEditorReadOnly(editorRef.current, readOnly);
    }
  }, [readOnly, setEditorReadOnly]);

  // Check if dirty props changed and trigger regeneration
  useEffect(() => {
    const currentProps = { modules, formats, bounds, theme, children };
    const shouldRegenerate = dirtyProps.some((prop) => {
      return !isEqual(currentProps[prop], prevPropsRef.current[prop]);
    });

    if (shouldRegenerate && prevPropsRef.current.modules !== undefined) {
      setGeneration((prev) => prev + 1);
    }

    prevPropsRef.current = currentProps;
  }, [modules, formats, bounds, theme, children]);

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    focus: () => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
    },
    blur: () => {
      if (editorRef.current) {
        selectionRef.current = null;
        editorRef.current.blur();
      }
    },
    getEditor: () => {
      if (!editorRef.current) {
        throw new Error('Accessing non-instantiated editor');
      }
      return editorRef.current;
    },
  }));

  // Render editing area
  const renderEditingArea = () => {
    if (React.Children.count(children)) {
      return React.cloneElement(React.Children.only(children), {
        key: generation,
        ref: editingAreaRef,
      });
    }

    return preserveWhitespace ? (
      <pre key={generation} ref={editingAreaRef} />
    ) : (
      <div key={generation} ref={editingAreaRef} />
    );
  };

  return (
    <div
      id={id}
      style={style}
      key={generation}
      className={`quill ${className}`}
      onKeyPress={onKeyPress}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      {renderEditingArea()}
    </div>
  );
});

ReactQuill.displayName = 'React Quill';

// Export Quill to be able to call `register`
ReactQuill.Quill = Quill;

export default ReactQuill;
